/**
 * Users Management API Client
 *
 * Enterprise-grade API client with:
 * - Strict typing using DTOs
 * - Proper error handling
 * - Centralized endpoint configuration
 */

// Types
export interface User {
  id: number;
  email: string;
  first_name: string;
  last_name: string;
  role?: string;
  company_id: number | null;
  is_active: boolean;
  created_at: string;
  updated_at: string;
  expires_at?: string;
  company_expires_at?: string;
}

export interface UserWithRoles extends User {
  permissions: string[];
  userTypes: number[];
  individualPermissions: string[];
  last_login_at?: string;
  last_login_ip?: string;
  is_online?: boolean;
  active_session_count?: number;
  company_name?: string;
  company?: { id: number; name: string } | null;
}

/**
 * Form state for user creation/editing
 * - id is optional (null for new users)
 * - All other fields are required for form validation
 */
export interface UserFormState {
  id: number | null;
  email: string;
  first_name: string;
  last_name: string;
  company_id: number | null;
  is_active: boolean;
  created_at: string;
  updated_at: string;
  permissions: string[];
}

export interface CreateUserData {
  email: string;
  first_name: string;
  last_name: string;
  password?: string; // Optional - server generates temporary password
  user_types?: number[]; // User can belong to multiple user types
  permissionKeys?: string[]; // Individual permission overrides (advanced)
  company_id?: number;
  partner_id?: number;
}

export interface UpdateUserData {
  first_name?: string;
  last_name?: string;
  user_types?: number[]; // User can belong to multiple user types
  permissionKeys?: string[]; // Individual permission overrides (advanced)
  company_id?: number;
  partner_id?: number;
  is_active?: boolean;
}

export interface GetAllUsersResponse {
  success: boolean;
  data: UserWithRoles[];
  pagination: {
    page: number;
    limit: number;
    total: number;
    pages: number;
  };
}

export interface GetUserResponse {
  success: boolean;
  data: UserWithRoles;
}

export interface CreateUserResponse {
  success: boolean;
  data: User;
}

export interface UpdateUserResponse {
  success: boolean;
  data: User;
}

export interface DeleteUserResponse {
  success: boolean;
  message: string;
}

export interface GetUsersByRoleResponse {
  success: boolean;
  data: User[];
}

/**
 * API Client Configuration
 */
const API_BASE = '/api/admin/users';

/**
 * Read the non-httpOnly CSRF cookie set by the server
 */
function getCsrfToken(): string | null {
  const match = document.cookie.match(/(?:^|;\s*)csrf_token=([^;]+)/);
  return match ? decodeURIComponent(match[1] as string) : null;
}

/**
 * Get authentication headers
 * Uses impersonation token if available, otherwise falls back to regular token.
 * Includes CSRF token for state-changing requests.
 */
function getAuthHeaders(includeCsrf = false): HeadersInit {
  // Try impersonation token first (for when admin is already impersonating)
  const impersonationToken = sessionStorage.getItem('impersonation_token');
  const token = localStorage.getItem('token');

  const authToken = impersonationToken || token;

  if (!authToken) {
    throw new Error('No authentication token found');
  }

  const headers: Record<string, string> = {
    'Authorization': `Bearer ${authToken}`,
    'Content-Type': 'application/json',
  };

  if (includeCsrf) {
    const csrfToken = getCsrfToken();
    if (csrfToken) headers['X-CSRF-Token'] = csrfToken;
  }

  return headers;
}

/** API error that carries the full response body so callers can inspect extra fields. */
export class ApiError extends Error {
  status: number;
  data: any;
  constructor(message: string, status: number, data: any) {
    super(message);
    this.name = 'ApiError';
    this.status = status;
    this.data = data;
  }
}

/**
 * Handle API response errors
 */
async function handleResponse<T>(response: Response): Promise<T> {
  if (!response.ok) {
    const data = await response.json().catch(() => ({ message: 'Unknown error' }));
    throw new ApiError(data.error || data.message || `API Error: ${response.status}`, response.status, data);
  }
  return response.json();
}

export const usersApi = {
  /**
   * Get all users
   */
  async getUsers(page: number = 1, limit: number = 20, search?: string, filters?: {
    company_id?: number;
    role?: string;
    is_active?: boolean;
  }): Promise<{
    users: UserWithRoles[];
    total: number;
    page: number;
    limit: number;
    pages: number;
  }> {
    const params = new URLSearchParams({
      page: page.toString(),
      limit: limit.toString(),
    });

    if (search) {
      params.append('search', search);
    }

    if (filters?.company_id) {
      params.append('company_id', filters.company_id.toString());
    }

    if (filters?.role) {
      params.append('role', filters.role);
    }

    if (filters?.is_active !== undefined) {
      params.append('is_active', filters.is_active.toString());
    }

    const response = await fetch(`${API_BASE}?${params}`, {
      headers: getAuthHeaders(),
    });

    const result: GetAllUsersResponse = await handleResponse(response);

    // Handle different response formats
    if (result.pagination) {
      // Full pagination response
      return {
        users: result.data,
        total: result.pagination.total,
        page: result.pagination.page,
        limit: result.pagination.limit,
        pages: result.pagination.pages,
      };
    } else {
      // Simple response without pagination
      return {
        users: result.data || result,
        total: (result.data || result).length,
        page: 1,
        limit: (result.data || result).length,
        pages: 1,
      };
    }
  },

  /**
   * Get user by ID
   */
  async getUserById(id: number): Promise<UserWithRoles> {
    const response = await fetch(`${API_BASE}/${id}`, {
      headers: getAuthHeaders(),
    });

    const result: GetUserResponse = await handleResponse(response);
    return result.data;
  },

  /**
   * Create user
   */
  async createUser(data: CreateUserData): Promise<User> {
    const response = await fetch(`${API_BASE}`, {
      method: 'POST',
      headers: getAuthHeaders(true),
      body: JSON.stringify(data),
    });

    const result: CreateUserResponse = await handleResponse(response);
    return result.data;
  },

  /**
   * Update user
   */
  async updateUser(id: number, data: UpdateUserData): Promise<User> {
    const response = await fetch(`${API_BASE}/${id}`, {
      method: 'PUT',
      headers: getAuthHeaders(true),
      body: JSON.stringify(data),
    });

    const result: UpdateUserResponse = await handleResponse(response);
    return result.data;
  },

  /**
   * Get permissions that the current user can assign to others
   */
  async getAssignablePermissions(): Promise<string[]> {
    const response = await fetch(`${API_BASE}/assignable-permissions`, {
      method: 'GET',
      headers: getAuthHeaders(),
    });

    const result: { success: boolean; data: string[] } = await handleResponse(response);
    return result.data;
  },

  /**
   * Get all system permissions
   */
  async getAllPermissions(): Promise<string[]> {
    const response = await fetch(`${API_BASE}/all-permissions`, {
      method: 'GET',
      headers: getAuthHeaders(),
    });

    const result: { success: boolean; data: string[] } = await handleResponse(response);
    return result.data;
  },

  /**
   * Get all user types
   */
  async getUserTypes(includeAll: boolean = false): Promise<any[]> {
    const url = includeAll ? `${API_BASE}/usertypes?includeAll=true` : `${API_BASE}/usertypes`;
    const response = await fetch(url, {
      method: 'GET',
      headers: getAuthHeaders(),
    });

    const result: { success: boolean; data: any[] } = await handleResponse(response);
    return result.data;
  },

  /**
   * Create a new user type
   */
  async createUserType(userTypeData: { name: string; description?: string; permissions: string[]; companyId?: number }): Promise<any> {
    const response = await fetch(`${API_BASE}/usertypes`, {
      method: 'POST',
      headers: getAuthHeaders(true),
      body: JSON.stringify(userTypeData),
    });

    const result: { success: boolean; data: any } = await handleResponse(response);
    return result.data;
  },

  /**
   * Update a user type
   */
  async updateUserType(id: number, userTypeData: { name: string; description?: string; permissions: string[]; is_active?: number }): Promise<any> {
    const response = await fetch(`${API_BASE}/usertypes/${id}`, {
      method: 'PUT',
      headers: getAuthHeaders(true),
      body: JSON.stringify(userTypeData),
    });

    const result: { success: boolean; data: any } = await handleResponse(response);
    return result.data;
  },

  /**
   * Delete a user type
   */
  async deleteUserType(id: number): Promise<any> {
    const response = await fetch(`${API_BASE}/usertypes/${id}`, {
      method: 'DELETE',
      headers: getAuthHeaders(true),
    });

    const result: { success: boolean; data: any } = await handleResponse(response);
    return result.data;
  },

  /**
   * Clone a permission profile (creates editable copy for the company)
   */
  async cloneUserType(id: number, newName: string): Promise<any> {
    const response = await fetch(`${API_BASE}/usertypes/${id}/clone`, {
      method: 'POST',
      headers: getAuthHeaders(true),
      body: JSON.stringify({ name: newName }),
    });

    const result: { success: boolean; data: any } = await handleResponse(response);
    return result.data;
  },



  /**
   * Get users assigned to a role
   */
  async getUsersByRole(roleId: number): Promise<User[]> {
    const response = await fetch(`/api/admin/users/roles/${roleId}/users`, {
      headers: getAuthHeaders(),
    });

    const result: GetUsersByRoleResponse = await handleResponse(response);
    return result.data;
  },

  /**
   * Assign role to user
   */
  async assignRoleToUser(userId: number, roleId: number): Promise<{ success: boolean; message: string }> {
    const response = await fetch(`${API_BASE}/${userId}/roles`, {
      method: 'POST',
      headers: getAuthHeaders(true),
      body: JSON.stringify({ roleId }),
    });

    return handleResponse(response);
  },

  /**
   * Remove role from user
   */
  async removeRoleFromUser(userId: number, roleId: number): Promise<{ success: boolean; message: string }> {
    const response = await fetch(`${API_BASE}/${userId}/roles/${roleId}`, {
      method: 'DELETE',
      headers: getAuthHeaders(true),
    });

    return handleResponse(response);
  },

  /**
   * Get users by company
   */
  async getUsersByCompany(companyId: number): Promise<UserWithRoles[]> {
    // Use the existing getUsers method with company filter
    const result = await this.getUsers(1, 1000, undefined, { company_id: companyId });
    return result.users;
  },

  /**
   * Deactivate user
   */
  async deactivateUser(userId: number, reason: string): Promise<{ success: boolean; message: string }> {
    const response = await fetch(`${API_BASE}/${userId}/deactivate`, {
      method: 'POST',
      headers: getAuthHeaders(true),
      body: JSON.stringify({ reason }),
    });

    return handleResponse(response);
  },

  /**
   * Reactivate user
   */
  async reactivateUser(userId: number): Promise<{ success: boolean; message: string }> {
    const response = await fetch(`${API_BASE}/${userId}/reactivate`, {
      method: 'POST',
      headers: getAuthHeaders(true),
    });

    return handleResponse(response);
  },

  /**
   * Kill all active sessions for a user (force logout)
   */
  async killUserSessions(userId: number): Promise<{ success: boolean; message: string; data: { killed: number } }> {
    const response = await fetch(`${API_BASE}/${userId}/kill-sessions`, {
      method: 'POST',
      headers: getAuthHeaders(true),
    });

    return handleResponse(response);
  },

  /**
   * Delete user (permanent removal)
   */
  async deleteUser(userId: number): Promise<{ success: boolean; message: string; details?: string }> {
    const response = await fetch(`${API_BASE}/${userId}`, {
      method: 'DELETE',
      headers: getAuthHeaders(true),
    });

    return handleResponse(response);
  },

  /**
   * Suspend user
   */
  async suspendUser(userId: number, reason: string): Promise<{ success: boolean; message: string }> {
    const response = await fetch(`${API_BASE}/${userId}/suspend`, {
      method: 'POST',
      headers: getAuthHeaders(true),
      body: JSON.stringify({ reason }),
    });

    return handleResponse(response);
  },

  /**
   * Reset user credentials
   */
  async resetUserCredentials(userId: number): Promise<{ success: boolean; message: string }> {
    const response = await fetch(`${API_BASE}/${userId}/reset-credentials`, {
      method: 'POST',
      headers: getAuthHeaders(true),
    });

    return handleResponse(response);
  },

  /**
   * Get user sessions
   */
  async getUserSessions(userId: number): Promise<{ success: boolean; data: any[] }> {
    const response = await fetch(`${API_BASE}/${userId}/sessions`, {
      method: 'GET',
      headers: getAuthHeaders(),
    });

    return handleResponse(response);
  }
};
